<template name="scan-attendee">
  <v-container fluid>
    <v-snackbar
      v-model="snackbar"
      class="elevation-24 text-center"
      vertical
      :color="snackbarColor"
    >
      <div class="text-subtitle-1 pb-2 text-white">ALERT</div>

      <p class="text-center text-white">{{ snackbarText }}</p>

      <template v-slot:actions>
        <v-btn
          color="indigo"
          variant="text"
          class="text-white"
          @click="snackbar = false"
        >
          <span class="text-white"> Close </span>
        </v-btn>
      </template>
    </v-snackbar>

    <v-row align="stretch" justify="center">
      <v-col cols="12" sm="12" md="6">
        <v-card class="ma-3 pa-3" variant="outlined" color="primary">
          <qrcode-stream
            :paused="paused"
            @detect="onDetect"
            @camera-on="onCameraOn"
            @camera-off="onCameraOff"
            @error="onError"
            :constraints="{
              facingMode: 'environment',
              width: 500,
              height: 500,
              track: true,
            }"
          >
            <div v-show="showScanConfirmation" class="scan-confirmation">
              Scan confirmation
            </div>
          </qrcode-stream>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="6">
        <v-card class="ma-3 pa-3" variant="outlined" color="primary">
          <v-card-title>Scan QR Code</v-card-title>
          <v-card-text>
            <p>Scan the QR code to check in attendees.</p>
          </v-card-text>
          <v-text-field
            v-model="result"
            label="QR Code Result"
            ref="qr"
            outlined
            class="mt-3"
            :rules="[() => (valid ? true : 'Please scan a QR code')]"
            :error-messages="valid ? [] : ['Please scan a QR code']"
            @click:clear="result = ''"
            @change="handleChange"
            @focus="valid = true"
            @blur="valid = false"
            @dragover="draggingOver = true"
            @dragleave="draggingOver = false"
            @drop="draggingOver = false"
            @dragenter="draggingOver = true"
            @dragend="draggingOver = false"
            @drag="draggingOver = true"
          >
          </v-text-field>
        </v-card>

        <v-card border="opacity-50 sm" class="pa-3" variant="outlined">
          <template v-slot:text>
            <v-text-field
              v-model="search"
              label="Search"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              hide-details
              single-line
            ></v-text-field>
          </template>
          <v-card-title>Invitations List</v-card-title>
          <v-data-table
            :headers="headers"
            :items="invitations"
            item-key="id"
            :items-per-page="5"
            :search="search"
          >
            <template v-slot:[`item.checked_in`]="{ item }">
              <v-checkbox
                v-model="item.checked_in"
                :true-value="1"
                :false-value="0"
                readonly
                color="success"
              ></v-checkbox>
            </template>
            <template v-slot:[`item.checkin_time`]="{ item }">
              {{ formatDateTime(item.checkin_time) }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <!-- Generate QR Code for Invitation -->
              <v-btn @click="generateQrCode(item.id)" color="primary" small>
                Generate QR
              </v-btn>
              <!-- Delete Invitation -->
              <v-btn @click="deleteInvitation(item.id)" color="orange" small>
                Delete
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { QrcodeStream } from "vue-qrcode-reader";
import axios from "axios";

export default {
  components: {
    QrcodeStream,
  },
  data() {
    return {
      result: "",
      search: "",
      paused: false,
      showScanConfirmation: false,
      draggingOver: false,
      valid: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      invitations: [],
      headers: [
        { title: "Name", value: "name", sortable: true },
        { title: "Email", value: "email" },
        { title: "Event", value: "event_name" },
        { title: "Checked In", value: "checked_in" },
        { title: "Checked Time", value: "checkin_time" },
      ],
    };
  },
  mounted() {
    this.fetchEvents();
    this.fetchInvitations();
    this.$nextTick(() => {
      this.$refs.qr.focus();
    });
  },
  methods: {
    async handleChange() {
      axios
        .post("/api/checkin", {
          qrData: this.result.trim(),
        })
        .then((response) => {
          console.log("Check-in response:", response.data);
          this.snackbarText = response.data.message;
          this.snackbar = true;
          this.snackbarColor = "success";
          this.fetchInvitations(); // Refresh the invitations list
        })
        .catch((error) => {
          this.snackbarText = error.response.data.message;
          this.snackbar = true;
          if (error.response.data.message == "Attendee not found") {
            this.snackbarColor = "red";
          } else {
            this.snackbarColor = "orange";
          }
          console.error("Check-in error:", error);
        });

      this.paused = true;
      await this.timeout(500);
      this.paused = false;
      this.result = "";
    },
    formatDateTime(datetime) {
      const date = new Date(datetime);

      const pad = (n) => n.toString().padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1); // Months are 0-based
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    onCameraOn() {
      this.showScanConfirmation = false;
    },

    onCameraOff() {
      this.showScanConfirmation = true;
    },

    onError: console.error,

    async onDetect(detectedCodes) {
      console.log("Detected codes:", detectedCodes);

      this.result = JSON.parse(
        JSON.stringify(detectedCodes.map((code) => code.rawValue))
      )[0];
      axios
        .post("/api/checkin", {
          qrData: this.result,
        })
        .then((response) => {
          console.log("Check-in response:", response.data);
          this.snackbarText = response.data.message;
          this.snackbar = true;
          this.snackbarColor = "success";
          this.result = "";

          this.fetchInvitations(); // Refresh the invitations list
        })
        .catch((error) => {
          this.snackbarText = error.response.data.message;
          this.snackbar = true;
          this.snackbarColor = "orange";
          this.result = "";
          console.error("Check-in error:", error);
        });

      this.paused = true;
      await this.timeout(500);
      this.paused = false;
    },

    timeout(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    },

    // Fetch invitations from API
    async fetchInvitations() {
      try {
        const response = await axios.get("/api/attendees");
        this.invitations = response.data.map((inv) => ({
          ...inv,
          eventName: this.getEventNameById(inv.eventId), // Display event name in the table
        }));
      } catch (error) {
        console.error("Error fetching attendees:", error);
      }
    },
    // Fetch events from API (you can modify this to match your backend)
    async fetchEvents() {
      try {
        const response = await axios.get("/api/events");
        this.events = response.data;
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    },
    // Get event name by event ID (simplified, assumes all events are fetched)
    getEventNameById(eventId) {
      const event = this.events.find((e) => e.id == eventId);
      return event ? event.name : "Unknown Event";
    },
  },
};
</script>


<style scoped>
.v-snackbar__wrapper {
  border-radius: 20%;
}
</style>